import React, {useEffect, useState} from 'react';
import {
    CircularProgress,
    List,
    ListItem,
    ListItemAvatar,
    ListItemButton,
    ListItemText,
    Paper,
    Stack,
    Typography
} from '@mui/material';
import {getUsersByRole} from '../../../firebase/firestore/users';
import SendMessageButton from "../../../components/SendMessageButton";
import UserAvatar from '../../../components/UserAvatar';
import {useNavigate} from "react-router";

const capitalize = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : '');

const prettyRole = (r) => {
    if (!r) return '';
    // convert snake_case or lower-case to Title Case
    return r.split(/_|\s+/).map(part => capitalize(part)).join(' ');
};

const getDisplayName = (u) => {
    if (!u) return '';
    // prefer displayName, then name, then first/last
    if (u.displayName) return u.displayName;
    if (u.name) return u.name;
    const first = u.firstName ? capitalize(u.firstName) : '';
    const last = u.lastName ? capitalize(u.lastName) : '';
    const combined = `${first} ${last}`.trim();
    return combined || u.email || u.id || 'Unknown';
};

const IcrExpertsList = ({icr, role = 'analyst'}) => {
    const navigate = useNavigate();

    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        let mounted = true;
        const load = async () => {
            setError(null);
            setLoading(true);
            try {
                const res = await getUsersByRole(role);
                if (mounted) setUsers(res || []);
            } catch (e) {
                if (mounted) setError(e?.message || String(e));
            } finally {
                if (mounted) setLoading(false);
            }
        };
        load();
        return () => {
            mounted = false;
        };
    }, [role]);

    // prefer explicit experts from `icr` if provided, otherwise show users fetched by role
    const experts = (icr?.experts && icr.experts.length > 0) ? icr.experts : users;

    const title = role ? (role.charAt(0).toUpperCase() + role.slice(1) + 's') : 'Users';

    return (
        <Paper elevation={0}
               sx={{flex: 1, pb: 1, borderRadius: 2, border: '1px solid', borderColor: 'divider'}}>
            <Stack p={2} spacing={1}>
                <Typography fontWeight={600}>Suggested experts</Typography>
                <Typography>
                    List of analysts who might be able to assist with this information collection request.
                </Typography>
                {loading ? (
                    <Stack direction="row" alignItems="center" spacing={1} pt={0.5}>
                        <CircularProgress size={18}/>
                        <Typography variant="body2" color="text.secondary">Loading...</Typography>
                    </Stack>
                ) : error ? (
                    <Typography variant="body2" color="error">{error}</Typography>
                ) : experts.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                        No {title.toLowerCase()} found
                    </Typography>
                ) : null}
            </Stack>

            {!loading && !error && experts.length > 0 && (
                <List disablePadding>
                    {experts.map((e, idx) => {
                        const id = e?.id || e?.uid || e?.email || idx;
                        const displayName = getDisplayName(e);
                        const secondary = e?.email || prettyRole(e?.role);

                        return (
                            <ListItem key={id} alignItems="flex-start"
                                      disablePadding

                                      secondaryAction={
                                          <SendMessageButton
                                              isIcon={true}
                                              user={{
                                                  userDisplayName: e.displayName,
                                                  displayName: e.displayName,
                                                  headshot: e.headshot,
                                                  agency: e.agency,
                                                  office: e.office,
                                                  poc: e.poc,
                                                  contact: e.contact,
                                                  title: e.userTitle,
                                                  email: e.email,
                                                  uid: e.id,
                                              }}
                                          />
                                      }
                            >
                                <ListItemButton
                                    onClick={() => navigate(`/experts/${e.displayName.toLowerCase().replace(' ', '.')}`)}
                                >
                                    <ListItemAvatar>
                                        <UserAvatar user={e} userId={id}/>
                                    </ListItemAvatar>
                                    <ListItemText
                                        primary={displayName}
                                        secondary={secondary}
                                    />
                                </ListItemButton>
                            </ListItem>
                        );
                    })}
                </List>
            )}
        </Paper>
    );
};

export default IcrExpertsList;
